import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Icon from '../../components/DS/Icon';
import SEO from '../../components/SEO';
import { fetchAllHotels } from '../../services/hotelService';
import { fetchAllTemples } from '../../services/templeService';
import { fetchAllGhats } from '../../services/ghatService';
import { fetchAllPandits } from '../../services/epujaService';
import {
  createHotel, updateHotel, deleteHotel,
  createTemple, updateTemple, deleteTemple,
  createGhat, updateGhat, deleteGhat,
  createPandit, updatePandit, deletePandit,
} from '../../services/staffService';

const STAFF_ROLES = ['admin', 'manager', 'entry_operator', 'field_staff', 'hotel_manager'];

const TABS = [
  { key: 'hotels',  label: 'Hotels',  icon: 'home' },
  { key: 'temples', label: 'Temples', icon: 'temple' },
  { key: 'ghats',   label: 'Ghats',   icon: 'water' },
  { key: 'pandits', label: 'Pandits', icon: 'sparkle' },
];

const HOTEL_CATEGORIES = ['budget', 'standard', 'premium', 'luxury', 'heritage', 'boutique'];
const PANDIT_SPECIALIZATIONS = [
  'vedic-rituals', 'havan-specialist', 'astrology', 'vastu',
  'katha-vachak', 'bhajan-mandali', 'marriage-specialist', 'pitru-karma', 'general',
];
const AMENITIES_LIST = ['wifi', 'parking', 'restaurant', 'ac', 'pool', 'spa', 'gym', 'laundry', 'elevator', 'garden'];
const LANGUAGES_LIST = ['Hindi', 'Sanskrit', 'English', 'Bhojpuri', 'Awadhi', 'Gujarati', 'Marathi'];

// ── Field configs per entity ─────────────────────────────────────────────────

const HOTEL_FIELDS = [
  { key: 'name',        label: 'Hotel Name',      type: 'text',     required: true },
  { key: 'description', label: 'Description',     type: 'textarea' },
  { key: 'address',     label: 'Address',         type: 'text' },
  { key: 'location',    label: 'Area / Location', type: 'text',     placeholder: 'e.g. Civil Lines, Ayodhya' },
  { key: 'phoneNumber', label: 'Phone',           type: 'tel' },
  { key: 'email',       label: 'Email',           type: 'email' },
  { key: 'website',     label: 'Website',         type: 'url' },
  { key: 'category',    label: 'Category',        type: 'select',   options: HOTEL_CATEGORIES },
  { key: 'starRating',  label: 'Star Rating',     type: 'number',   min: 1, max: 5 },
  { key: 'priceRange',  label: 'Price Range',     type: 'text',     placeholder: 'e.g. ₹2,000 – ₹8,000' },
  { key: 'totalRooms',  label: 'Total Rooms',     type: 'number',   min: 1 },
  { key: 'amenities',   label: 'Amenities',       type: 'checkboxes', options: AMENITIES_LIST },
  { key: 'images',      label: 'Image URLs',      type: 'taglist',  placeholder: 'Paste image URL and press Enter' },
  { key: 'latitude',    label: 'Latitude',        type: 'number',   step: 'any', placeholder: '26.7922' },
  { key: 'longitude',   label: 'Longitude',       type: 'number',   step: 'any', placeholder: '82.1998' },
  { key: 'featured',    label: 'Featured',        type: 'toggle' },
  { key: 'isBookable',  label: 'Bookable Online', type: 'toggle' },
];

const TEMPLE_FIELDS = [
  { key: 'name',         label: 'Temple Name',   type: 'text',     required: true },
  { key: 'description',  label: 'Description',   type: 'textarea' },
  { key: 'address',      label: 'Address',       type: 'text' },
  { key: 'location',     label: 'Area',          type: 'text' },
  { key: 'history',      label: 'History',       type: 'textarea' },
  { key: 'significance', label: 'Significance',  type: 'textarea' },
  { key: 'images',       label: 'Image URLs',    type: 'taglist',  placeholder: 'Paste image URL and press Enter' },
  { key: 'featured',     label: 'Featured',      type: 'toggle' },
];

const GHAT_FIELDS = [
  { key: 'name',          label: 'Ghat Name',        type: 'text',     required: true },
  { key: 'description',   label: 'Description',      type: 'textarea' },
  { key: 'address',       label: 'Address',          type: 'text' },
  { key: 'location',      label: 'Area',             type: 'text' },
  { key: 'openingHours',  label: 'Opening Time',     type: 'text',     placeholder: 'e.g. 5:00 AM' },
  { key: 'closingHours',  label: 'Closing Time',     type: 'text',     placeholder: 'e.g. 10:00 PM' },
  { key: 'facilities',    label: 'Facilities',       type: 'taglist',  placeholder: 'e.g. Changing Room, Locker' },
  { key: 'capacity',      label: 'Capacity (people)',type: 'number',   min: 0 },
  { key: 'images',        label: 'Image URLs',       type: 'taglist',  placeholder: 'Paste image URL and press Enter' },
  { key: 'latitude',      label: 'Latitude',         type: 'number',   step: 'any' },
  { key: 'longitude',     label: 'Longitude',        type: 'number',   step: 'any' },
  { key: 'featured',      label: 'Featured',         type: 'toggle' },
];

const PANDIT_FIELDS = [
  { key: 'name',              label: 'Full Name',          type: 'text',     required: true },
  { key: 'nameHindi',         label: 'Name (Hindi)',       type: 'text',     placeholder: 'पं. नाम' },
  { key: 'slug',              label: 'URL Slug',           type: 'text',     placeholder: 'pt-ramesh-sharma' },
  { key: 'bio',               label: 'Bio / About',        type: 'textarea' },
  { key: 'profileImage',      label: 'Profile Photo URL',  type: 'url' },
  { key: 'specialization',    label: 'Specialization',     type: 'select',   options: PANDIT_SPECIALIZATIONS },
  { key: 'experienceYears',   label: 'Experience (years)', type: 'number',   min: 0 },
  { key: 'languages',         label: 'Languages',          type: 'checkboxes', options: LANGUAGES_LIST },
  { key: 'phoneNumber',       label: 'Phone',              type: 'tel' },
  { key: 'location',          label: 'Location',           type: 'text' },
  { key: 'hourlyRate',        label: 'Hourly Rate (₹)',     type: 'number',   min: 0 },
  { key: 'qualifications',    label: 'Qualifications',     type: 'taglist',  placeholder: 'Add qualification and press Enter' },
  { key: 'availableOnline',   label: 'Available Online',   type: 'toggle' },
  { key: 'availableOffline',  label: 'Available In-person',type: 'toggle' },
  { key: 'isVerified',        label: 'Verified',           type: 'toggle' },
  { key: 'featured',          label: 'Featured',           type: 'toggle' },
];

const ENTITY_CONFIG = {
  hotels:  { fields: HOTEL_FIELDS,  create: createHotel,  update: updateHotel,  remove: deleteHotel,  nameKey: 'name', subtitleKey: 'location' },
  temples: { fields: TEMPLE_FIELDS, create: createTemple, update: updateTemple, remove: deleteTemple, nameKey: 'name', subtitleKey: 'location' },
  ghats:   { fields: GHAT_FIELDS,   create: createGhat,   update: updateGhat,   remove: deleteGhat,   nameKey: 'name', subtitleKey: 'location' },
  pandits: { fields: PANDIT_FIELDS, create: createPandit, update: updatePandit, remove: deletePandit, nameKey: 'name', subtitleKey: 'specialization' },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function getInitialFormData(fields) {
  const data = {};
  fields.forEach(f => {
    if (f.type === 'toggle') data[f.key] = false;
    else if (f.type === 'checkboxes' || f.type === 'taglist') data[f.key] = [];
    else if (f.type === 'number') data[f.key] = '';
    else data[f.key] = '';
  });
  return data;
}

function populateForm(fields, item) {
  const data = getInitialFormData(fields);
  fields.forEach(f => {
    if (item[f.key] !== undefined && item[f.key] !== null) {
      data[f.key] = item[f.key];
    }
  });
  return data;
}

function cleanPayload(formData, fields) {
  const payload = {};
  fields.forEach(f => {
    const v = formData[f.key];
    if (f.type === 'toggle') { payload[f.key] = !!v; return; }
    if (f.type === 'checkboxes' || f.type === 'taglist') {
      if (Array.isArray(v) && v.length > 0) payload[f.key] = v;
      return;
    }
    if (f.type === 'number') {
      if (v !== '' && v !== null && v !== undefined) payload[f.key] = Number(v);
      return;
    }
    if (v && String(v).trim()) payload[f.key] = String(v).trim();
  });
  return payload;
}

// ── Sub-components ───────────────────────────────────────────────────────────

function TagListInput({ value = [], onChange, placeholder }) {
  const [input, setInput] = useState('');

  const add = () => {
    const v = input.trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setInput('');
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input
          className="ad-input"
          style={{ height: 38, flex: 1 }}
          value={input}
          placeholder={placeholder}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <button type="button" className="ad-btn ghost" style={{ height: 38, padding: '0 14px' }} onClick={add}>Add</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {value.map(tag => (
          <span key={tag} className="ad-chip" style={{ gap: 4 }}>
            {tag}
            <button
              type="button"
              onClick={() => onChange(value.filter(t => t !== tag))}
              style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: 'var(--ink-3)' }}
            >
              <Icon name="close" size={10} stroke="currentColor" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

function CheckboxGroup({ options, value = [], onChange }) {
  const toggle = (opt) => onChange(
    value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]
  );
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map(opt => (
        <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          padding: '5px 12px', borderRadius: 99, border: `1px solid ${value.includes(opt) ? 'var(--saffron)' : 'var(--line)'}`,
          background: value.includes(opt) ? 'var(--saffron-soft)' : 'transparent',
          fontSize: 13, color: value.includes(opt) ? 'var(--saffron-ink)' : 'var(--ink-2)', transition: 'all .12s',
        }}>
          <input type="checkbox" checked={value.includes(opt)} onChange={() => toggle(opt)} style={{ display: 'none' }} />
          {opt}
        </label>
      ))}
    </div>
  );
}

function Toggle({ value, onChange, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
      <div
        onClick={() => onChange(!value)}
        style={{
          width: 44, height: 24, borderRadius: 99, position: 'relative', transition: 'background .2s',
          background: value ? 'var(--saffron)' : 'var(--line)', cursor: 'pointer', flexShrink: 0,
        }}
      >
        <div style={{
          position: 'absolute', top: 3, left: value ? 23 : 3, width: 18, height: 18,
          borderRadius: '50%', background: '#fff', transition: 'left .2s',
          boxShadow: '0 1px 3px rgba(0,0,0,.2)',
        }} />
      </div>
      <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>{label}</span>
    </label>
  );
}

function FormField({ field, value, onChange }) {
  const inputStyle = { height: 42 };

  if (field.type === 'textarea') return (
    <textarea
      className="ad-input"
      style={{ height: 100, resize: 'vertical', padding: '10px 14px' }}
      value={value || ''}
      placeholder={field.placeholder}
      onChange={e => onChange(e.target.value)}
    />
  );

  if (field.type === 'select') return (
    <select className="ad-input" style={inputStyle} value={value || ''} onChange={e => onChange(e.target.value)}>
      <option value="">— Select —</option>
      {field.options.map(o => (
        <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1).replace(/-/g, ' ')}</option>
      ))}
    </select>
  );

  if (field.type === 'checkboxes') return (
    <CheckboxGroup options={field.options} value={value || []} onChange={onChange} />
  );

  if (field.type === 'taglist') return (
    <TagListInput value={value || []} onChange={onChange} placeholder={field.placeholder} />
  );

  if (field.type === 'toggle') return (
    <Toggle value={!!value} onChange={onChange} label={value ? 'Yes' : 'No'} />
  );

  return (
    <input
      className="ad-input"
      style={inputStyle}
      type={field.type}
      value={value ?? ''}
      placeholder={field.placeholder}
      min={field.min}
      max={field.max}
      step={field.step}
      onChange={e => onChange(e.target.value)}
    />
  );
}

// ── Entity Form Panel ────────────────────────────────────────────────────────

function EntityFormPanel({ entityType, item, onClose, onSaved }) {
  const config = ENTITY_CONFIG[entityType];
  const isEdit = !!item;
  const [formData, setFormData] = useState(() =>
    isEdit ? populateForm(config.fields, item) : getInitialFormData(config.fields)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setField = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    const nameVal = formData[config.fields.find(f => f.required)?.key || 'name'];
    if (!nameVal?.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = cleanPayload(formData, config.fields);
      const result = isEdit
        ? await config.update(item.id, payload)
        : await config.create(payload);
      onSaved(result, isEdit);
    } catch (e) {
      setError(e?.response?.data?.message || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
    }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.4)', backdropFilter: 'blur(2px)' }}
      />

      {/* Panel */}
      <div style={{
        position: 'relative', zIndex: 1,
        width: '100%', maxWidth: 520,
        height: '100dvh', maxHeight: '100dvh',
        background: 'var(--surface)', borderLeft: '1px solid var(--line)',
        display: 'flex', flexDirection: 'column',
        animation: 'adFadeUp .2s ease',
      }}>
        {/* Panel header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--line)', flexShrink: 0 }}>
          <div>
            <div className="ad-mono" style={{ marginBottom: 2 }}>{isEdit ? 'Edit' : 'Add New'}</div>
            <div className="ad-serif" style={{ fontSize: 18, color: 'var(--ink)' }}>
              {entityType.charAt(0).toUpperCase() + entityType.slice(1, -1)}
            </div>
          </div>
          <button className="ad-btn ghost" style={{ width: 36, height: 36, padding: 0 }} onClick={onClose}>
            <Icon name="close" size={16} />
          </button>
        </div>

        {/* Form body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {config.fields.map(field => (
            <div key={field.key} style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12.5, fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-3)', marginBottom: 7 }}>
                {field.label}
                {field.required && <span style={{ color: 'var(--saffron)', marginLeft: 4 }}>*</span>}
              </label>
              <FormField field={field} value={formData[field.key]} onChange={val => setField(field.key, val)} />
            </div>
          ))}

          {error && (
            <div style={{ padding: '10px 14px', borderRadius: 'var(--r-md)', background: 'oklch(95% 0.04 22)', border: '1px solid oklch(85% 0.08 22)', color: 'oklch(38% 0.09 22)', fontSize: 13, marginBottom: 12 }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--line)', flexShrink: 0, display: 'flex', gap: 10 }}>
          <button className="ad-btn ghost" style={{ flex: 1, height: 44, justifyContent: 'center' }} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="ad-btn saffron"
            style={{ flex: 2, height: 44, justifyContent: 'center' }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add & Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Entity List ──────────────────────────────────────────────────────────────

function EntityList({ entityType, items, loading, onEdit, onDelete }) {
  const config = ENTITY_CONFIG[entityType];
  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <div className="ad-mono" style={{ animation: 'adPulse 1.5s ease infinite' }}>Loading…</div>
    </div>
  );
  if (items.length === 0) return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <div className="ad-serif" style={{ fontSize: 20, color: 'var(--ink-3)', marginBottom: 6 }}>Nothing here yet</div>
      <p style={{ fontSize: 13, color: 'var(--ink-4)' }}>Use the + button to add the first entry.</p>
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map(item => (
        <div key={item.id} className="ad-card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* Avatar / initials */}
          <div style={{
            width: 44, height: 44, borderRadius: 'var(--r-md)', flexShrink: 0,
            background: 'var(--saffron-soft)', border: '1px solid var(--saffron-line)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
          }}>
            {item.images?.[0] || item.profileImage
              ? <img src={item.images?.[0] || item.profileImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span className="ad-serif" style={{ fontSize: 16, color: 'var(--saffron-ink)' }}>
                  {(item[config.nameKey] || '?').charAt(0)}
                </span>
            }
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--ink)', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item[config.nameKey] || '—'}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item[config.subtitleKey] || item.address || item.location || '—'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button className="ad-btn ghost" style={{ height: 34, width: 34, padding: 0 }} onClick={() => onEdit(item)} title="Edit">
              <Icon name="edit" size={14} />
            </button>
            <button
              className="ad-btn ghost"
              style={{ height: 34, width: 34, padding: 0, borderColor: 'oklch(85% 0.08 22)', color: 'oklch(38% 0.09 22)' }}
              onClick={() => onDelete(item)}
              title="Delete"
            >
              <Icon name="trash" size={14} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Delete Confirm ────────────────────────────────────────────────────────────

function DeleteConfirm({ item, entityType, onConfirm, onCancel }) {
  const config = ENTITY_CONFIG[entityType];
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    setDeleting(true);
    try { await config.remove(item.id); onConfirm(item.id); }
    catch { setDeleting(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={onCancel} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.5)' }} />
      <div className="ad-card" style={{ position: 'relative', zIndex: 1, padding: 28, maxWidth: 380, width: '100%' }}>
        <div className="ad-serif" style={{ fontSize: 20, marginBottom: 10, color: 'var(--ink)' }}>Delete entry?</div>
        <p style={{ fontSize: 14, color: 'var(--ink-2)', marginBottom: 24, lineHeight: 1.5 }}>
          This will permanently delete <strong>{item[config.nameKey]}</strong>. This cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="ad-btn ghost" style={{ flex: 1, height: 42, justifyContent: 'center' }} onClick={onCancel}>Cancel</button>
          <button
            className="ad-btn"
            style={{ flex: 1, height: 42, justifyContent: 'center', background: 'oklch(50% 0.13 22)', color: '#fff' }}
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Staff Portal ────────────────────────────────────────────────────────

const FETCH_FNS = {
  hotels:  (params) => fetchAllHotels(params).then(d => d?.data || d || []),
  temples: ()      => fetchAllTemples().then(d => d?.data || d || []),
  ghats:   ()      => fetchAllGhats().then(d => d?.data || d || []),
  pandits: (params) => fetchAllPandits(params).then(d => d?.data || d || []),
};

export default function StaffPortal() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('hotels');
  const [data, setData] = useState({ hotels: [], temples: [], ghats: [], pandits: [] });
  const [loading, setLoading] = useState({ hotels: false, temples: false, ghats: false, pandits: false });
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState('');

  // Auth + role guard
  useEffect(() => {
    if (!isAuthenticated) { navigate('/login', { state: { from: '/staff' } }); return; }
    const role = user?.role?.name || user?.role;
    if (!STAFF_ROLES.includes(role)) navigate('/');
  }, [isAuthenticated, user, navigate]);

  const loadTab = useCallback(async (tab) => {
    setLoading(prev => ({ ...prev, [tab]: true }));
    try {
      const result = await FETCH_FNS[tab]({ limit: 100 });
      setData(prev => ({ ...prev, [tab]: Array.isArray(result) ? result : [] }));
    } catch {
      setData(prev => ({ ...prev, [tab]: [] }));
    } finally {
      setLoading(prev => ({ ...prev, [tab]: false }));
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) loadTab(activeTab);
  }, [activeTab, isAuthenticated, loadTab]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleSaved = (result, isEdit) => {
    setData(prev => {
      const list = prev[activeTab];
      return {
        ...prev,
        [activeTab]: isEdit
          ? list.map(i => i.id === result.id ? result : i)
          : [result, ...list],
      };
    });
    setFormOpen(false);
    setEditItem(null);
    showToast(isEdit ? 'Updated successfully.' : 'Added successfully.');
  };

  const handleDeleted = (id) => {
    setData(prev => ({ ...prev, [activeTab]: prev[activeTab].filter(i => i.id !== id) }));
    setDeleteItem(null);
    showToast('Deleted.');
  };

  const filtered = (data[activeTab] || []).filter(item =>
    !search || Object.values(item).some(v =>
      typeof v === 'string' && v.toLowerCase().includes(search.toLowerCase())
    )
  );

  return (
    <>
      <SEO title="Staff Portal — Ayodhyā Dhām" noindex />

      <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>

        {/* Top bar */}
        <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--line)', padding: '0 20px', display: 'flex', alignItems: 'center', height: 56, flexShrink: 0, position: 'sticky', top: 0, zIndex: 100 }}>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 0, cursor: 'pointer', marginRight: 16, display: 'flex', alignItems: 'center' }}>
            <Icon name="arrow_left" size={16} stroke="var(--ink-3)" />
          </button>
          <div className="ad-serif" style={{ fontSize: 18, color: 'var(--ink)', flex: 1 }}>Staff Portal</div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
            {user?.name || user?.email || 'Staff'}
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--line)', display: 'flex', overflowX: 'auto', flexShrink: 0 }}>
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: '0 0 auto', height: 48, padding: '0 20px', border: 0, background: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontFamily: 'var(--font-sans)',
                color: activeTab === tab.key ? 'var(--saffron)' : 'var(--ink-3)',
                borderBottom: `2px solid ${activeTab === tab.key ? 'var(--saffron)' : 'transparent'}`,
                transition: 'color .12s',
              }}
            >
              <Icon name={tab.icon} size={14} stroke="currentColor" />
              {tab.label}
              <span style={{ fontSize: 11, background: 'var(--surface-2)', borderRadius: 99, padding: '1px 7px', color: 'var(--ink-3)' }}>
                {data[tab.key]?.length || 0}
              </span>
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div style={{ padding: '16px 20px 12px', display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Icon name="search" size={14} stroke="var(--ink-4)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            <input
              className="ad-input"
              style={{ height: 40, paddingLeft: 36 }}
              placeholder={`Search ${activeTab}…`}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button
            className="ad-btn saffron"
            style={{ height: 40, padding: '0 16px', gap: 6, flexShrink: 0 }}
            onClick={() => { setEditItem(null); setFormOpen(true); }}
          >
            <Icon name="plus" size={14} stroke="#fff" />
            Add
          </button>
        </div>

        {/* List */}
        <div style={{ flex: 1, padding: '0 20px 100px' }}>
          <EntityList
            entityType={activeTab}
            items={filtered}
            loading={loading[activeTab]}
            onEdit={item => { setEditItem(item); setFormOpen(true); }}
            onDelete={item => setDeleteItem(item)}
          />
        </div>
      </div>

      {/* Form panel */}
      {formOpen && (
        <EntityFormPanel
          entityType={activeTab}
          item={editItem}
          onClose={() => { setFormOpen(false); setEditItem(null); }}
          onSaved={handleSaved}
        />
      )}

      {/* Delete confirm */}
      {deleteItem && (
        <DeleteConfirm
          item={deleteItem}
          entityType={activeTab}
          onConfirm={handleDeleted}
          onCancel={() => setDeleteItem(null)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--ink)', color: 'var(--surface)', padding: '10px 20px',
          borderRadius: 99, fontSize: 13.5, zIndex: 2000, whiteSpace: 'nowrap',
          boxShadow: 'var(--shadow-2)', animation: 'adFadeUp .2s ease',
        }}>
          {toast}
        </div>
      )}
    </>
  );
}
